/**
 * MemberLoginPage - Login page for Founding Members
 * OTP-based login via email or phone
 * Features: Remember phone number, rate limit display
 */
import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, ArrowRight, Loader2, Coffee, CheckCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { api } from '@/utils/apiClient';

const STORAGE_KEY = 'bulaccino_member_phone';

export default function MemberLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/rewards';
  
  const [step, setStep] = useState('login'); // 'login' | 'verify' | 'success'
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' | 'email'
  const [phone, setPhone] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [rememberPhone, setRememberPhone] = useState(() => !!localStorage.getItem(STORAGE_KEY));
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  
  // Handle phone save/clear when user explicitly changes checkbox
  const handleRememberPhoneChange = (checked) => {
    setRememberPhone(checked);
    if (checked && phone) {
      localStorage.setItem(STORAGE_KEY, phone);
    } else if (!checked) {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    
    if (loginMethod === 'phone' && !phone) {
      toast.error('Please enter your phone number');
      return;
    }
    if (loginMethod === 'email' && !email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const payload = loginMethod === 'phone' 
        ? { phone: phone.trim() } 
        : { email: email.trim().toLowerCase() };
      
      const res = await api.post('/member-auth/login', payload);
      const data = res.data;
      
      // Save phone if "Remember" is checked
      if (rememberPhone && loginMethod === 'phone') {
        localStorage.setItem(STORAGE_KEY, phone);
      }
      
      // Update rate limit info
      if (data.rate_limit) {
        setRateLimitInfo(data.rate_limit);
      }
      
      toast.success('Verification code sent!', {
        description: data.message
      });
      setStep('verify');
    } catch (err) {
      if (err.response?.status === 429) {
        // Rate limited
        toast.error('Too many attempts', {
          description: err.response?.data?.detail || 'Please try again later'
        });
        setRateLimitInfo({ locked: true });
      } else if (err.response?.status === 404) {
        toast.error('Account not found', {
          description: 'Please register first to become a Founding Member',
          action: {
            label: 'Register',
            onClick: () => navigate('/join')
          }
        });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to send code');
      }
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length < 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        otp: otp.trim(),
        ...(loginMethod === 'phone' ? { phone: phone.trim() } : { email: email.trim().toLowerCase() })
      };
      
      const res = await api.post('/member-auth/verify', payload);
      const data = res.data;
      
      setUserId(data.user_id);
      setStep('success');
      toast.success(`Welcome back, ${data.name}!`);
      
      // Redirect after short delay
      setTimeout(() => {
        navigate(`/rewards?user_id=${data.user_id}`);
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setOtp('');
    await handleRequestOTP({ preventDefault: () => {} });
  };

  return (
    <>
      <Helmet>
        <title>Member Login - Bulaccino Cafe</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mx-auto flex items-center justify-center shadow-lg">
                <Coffee className="w-10 h-10 text-white" />
              </div>
            </Link>
            <h1 className="text-2xl font-bold mt-4 text-foreground">Founding Member Login</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Access your account to view referrals & rewards
            </p>
          </div>
          
          {/* Login Card */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-xl">
            
            {/* Step: Login */}
            {step === 'login' && (
              <form onSubmit={handleRequestOTP} className="space-y-4">
                {/* Toggle Phone/Email */}
                <div className="flex gap-2 p-1 bg-muted rounded-lg">
                  <button
                    type="button"
                    onClick={() => setLoginMethod('phone')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      loginMethod === 'phone'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMethod('email')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      loginMethod === 'email'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </button>
                </div>
                
                {/* Input Field */}
                {loginMethod === 'phone' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-foreground">Phone Number</label>
                      <Input
                        type="tel"
                        placeholder="Enter your phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="h-12"
                        data-testid="phone-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        We&apos;ll send a verification code via SMS
                      </p>
                    </div>
                    
                    {/* Remember Phone Checkbox */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember-phone"
                        checked={rememberPhone}
                        onCheckedChange={handleRememberPhoneChange}
                        data-testid="remember-phone-checkbox"
                      />
                      <label
                        htmlFor="remember-phone"
                        className="text-sm text-muted-foreground cursor-pointer select-none"
                      >
                        Remember my phone number
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-foreground">Email Address</label>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12"
                      data-testid="email-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      We&apos;ll send a verification code to your email
                    </p>
                  </div>
                )}
                
                {/* Rate Limit Warning */}
                {rateLimitInfo?.locked && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                    <p className="text-sm text-destructive">
                      Too many attempts. Please try again in 30 minutes.
                    </p>
                  </div>
                )}
                
                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-base bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Send Verification Code
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                {/* Register Link */}
                <p className="text-center text-sm text-muted-foreground">
                  Not a member yet?{' '}
                  <Link to="/join" className="text-primary font-medium hover:underline">
                    Register as Founding Member
                  </Link>
                </p>
              </form>
            )}
            
            {/* Step: Verify OTP */}
            {step === 'verify' && (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-lg font-semibold">Check your {loginMethod === 'phone' ? 'SMS' : 'email'}</h2>
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code sent to{' '}
                    <strong>{loginMethod === 'phone' ? phone : email}</strong>
                  </p>
                </div>
                
                {/* OTP Input */}
                <div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="h-14 text-center text-2xl tracking-widest font-mono"
                    data-testid="otp-input"
                    autoFocus
                  />
                </div>
                
                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading || otp.length < 6}
                  className="w-full h-12 text-base"
                  data-testid="verify-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Verify & Login
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                {/* Resend */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Resend code
                  </button>
                </div>
                
                {/* Back */}
                <button
                  type="button"
                  onClick={() => { setStep('login'); setOtp(''); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  ← Use different email/phone
                </button>
              </form>
            )}
            
            {/* Step: Success */}
            {step === 'success' && (
              <div className="text-center py-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
                </motion.div>
                <h2 className="text-xl font-bold text-green-600 dark:text-green-400">You&apos;re In!</h2>
                <p className="text-muted-foreground mt-2">Redirecting to your account...</p>
                <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-primary" />
              </div>
            )}
          </div>
          
          {/* Location Note */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            📍 Bulaccino Cafe, Concave Dr, Namaka, Nadi
          </p>
        </motion.div>
      </div>
    </>
  );
}
